import { DATA_TOPICS } from '../shared/ipc-contract'
import type { DataTopic } from '../shared/ipc-contract'

// The web portal's live-refresh transport: server-sent `data:changed`, plus the fallback for when it
// stops arriving.
//
// WHY IT IS THE SERVER'S JOB AT ALL. A desktop terminal hears about another till's sale on its 60s sync
// loop. Two browsers in one shop have no such loop and no shared process, so the server fans changes out
// over SSE (see src/server/routes/events.ts) and each screen refetches through the topics it already
// watches with useDataChanged.
//
// AND THE PART THIS MODULE EXISTS FOR. That stream is an accelerator; it must never be the only way data
// moves. It used to be. An EventSource whose connection is open but silent — a reverse proxy buffering
// the response, a half-open LISTEN socket on the server — raises no error and closes nothing, so every
// screen simply froze, indefinitely, and looked exactly like a shop where nobody had ordered anything.
// The watchdog below turns that into "stale for up to a minute", which is the guarantee the desktop has
// had all along and the same principle as src/core/sync/live.ts.
//
// Its own module rather than another function in main.ts: main.ts is the window.api shim and the
// bootstrap order, this is a socket with a lifecycle, and only one of the two is worth testing on its
// own.

/** No frame for this long, on a visible tab, means the stream is not delivering. */
const STREAM_SILENCE_MS = 45_000

/** How often that is checked. */
const WATCHDOG_MS = 20_000

/** Ceiling on the degraded full refresh, so a dead stream costs one refetch a minute, not three. */
const DEGRADED_MS = 60_000

/** Trailing debounce on the fan-out. */
const COALESCE_MS = 250

/**
 * Opens the stream and keeps `fanout` fed.
 *
 * The server heartbeats every 15s (HEARTBEAT_MS in src/server/routes/events.ts), so on a healthy
 * connection the watchdog never fires and costs nothing.
 *
 * Returns a teardown for tests; the app itself keeps this open for the life of the tab.
 */
export function openEventStream(fanout: (topics: DataTopic[]) => void): () => void {
  const es = new EventSource('/api/events')
  let hadError = false
  // Any frame counts as proof of life, heartbeats included — this is about whether the transport works,
  // not about whether anything happened to be published.
  let lastFrameAt = Date.now()
  let lastDegradedAt = 0
  let degradedWarned = false
  // Coalesce a burst into one fan-out. useDataChanged already debounces 150ms per hook, but that does
  // not absorb the N-browsers multiplier: one busy till can otherwise make every other browser refetch
  // once per mutation.
  let pending = new Set<DataTopic>()
  let timer: ReturnType<typeof setTimeout> | null = null

  const flush = (): void => {
    timer = null
    const topics = [...pending]
    pending = new Set()
    if (topics.length > 0) fanout(topics)
  }

  const queue = (topics: DataTopic[]): void => {
    for (const t of topics) pending.add(t)
    if (!timer) timer = setTimeout(flush, COALESCE_MS)
  }

  const sawFrame = (): void => {
    lastFrameAt = Date.now()
  }

  es.addEventListener('data:changed', (e) => {
    sawFrame()
    try {
      queue(JSON.parse((e as MessageEvent<string>).data) as DataTopic[])
    } catch {
      /* malformed frame — ignore rather than break the stream */
    }
  })

  // Carries nothing, and that is exactly its value: in a quiet shop the heartbeat is the only evidence
  // that the transport still works.
  es.addEventListener('ping', sawFrame)

  es.onerror = () => {
    // EventSource reconnects on its own; just remember that we were disconnected.
    hadError = true
  }

  es.addEventListener('ready', () => {
    sawFrame()
    if (!hadError) return
    hadError = false
    // We cannot know what was missed while disconnected, so invalidate everything. This is why
    // DATA_TOPICS exists as a runtime list.
    queue([...DATA_TOPICS])
  })

  const watchdog = setInterval(() => {
    // A hidden tab is not worth refetching for — nothing is on screen to be stale, and it comes back
    // through this same check on the tick after it is shown again.
    if (document.hidden) return
    const now = Date.now()
    if (now - lastFrameAt < STREAM_SILENCE_MS) return
    if (now - lastDegradedAt < DEGRADED_MS) return
    lastDegradedAt = now
    if (!degradedWarned) {
      degradedWarned = true
      console.warn(
        `[web] no event-stream traffic for ${Math.round(STREAM_SILENCE_MS / 1000)}s — falling back ` +
          'to a periodic refresh. Check /api/health: "listen":"stale" means the server cannot hear ' +
          'Postgres, while "ok" points at a proxy buffering /api/events.'
      )
    }
    // Same invalidate-everything move as a reconnect, and for the same reason: with no frames arriving
    // there is no way to know which topics moved.
    queue([...DATA_TOPICS])
  }, WATCHDOG_MS)

  return () => {
    clearInterval(watchdog)
    if (timer) clearTimeout(timer)
    es.close()
  }
}
